import type { FC, ReactNode, SyntheticEvent } from 'react';

import type { Clients } from '@/assets/clients/type';

import Chip from '../Chip';
import Stack from '../Landing/Stack';
import Technology from '../Landing/Technology';
import Text from '../Text';
import { clients } from './clients';
import { technologyCatalog } from './technologyCatalog';

type Props = {
  companyName: string;
  description: ReactNode;
  topImage: string;
  bottomImage: string;
  scope: ReactNode;
  industry: string;
  country: string;
  clientId: Clients;
};
const ClientPage: FC<Props> = ({
  companyName,
  description,
  bottomImage,
  topImage,
  scope,
  country,
  industry,
  clientId,
}) => {
  const { highlights, services, technologies } =
    clients.find(({ id }) => id === clientId) ?? {};

  const setPlaybackRate = (e: SyntheticEvent<HTMLVideoElement, Event>) => {
    e.currentTarget.playbackRate = 0.4;
  };

  return (
    <Stack className="pb-10">
      <Text variant="display-2" className="w-full text-jb-neutral-100">
        {companyName}
      </Text>
      <div className="flex w-full flex-col gap-10 lg:flex-row">
        <div className="flex-1">
          <div className="flex max-w-lg flex-col gap-6">
            <div className="flex w-full flex-row flex-wrap gap-2">
              {(highlights ?? []).map(({ text, variant }) => (
                <Chip key={text} variant={variant} className="w-fit" size="sm">
                  {text.toLocaleUpperCase()}
                </Chip>
              ))}
            </div>
            <div className="flex flex-col gap-1">{description}</div>
            <div className="flex flex-col gap-3">
              <Text variant="body" className="text-jb-neutral-100">
                Technologies
              </Text>
              <div className="flex flex-row flex-wrap gap-6">
                {technologies?.map((tech) => (
                  <Technology
                    key={tech}
                    icon={technologyCatalog[tech].icon}
                    name={technologyCatalog[tech].name}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-6">
          <video
            src={topImage}
            autoPlay
            loop
            muted
            playsInline
            className="h-auto w-full rounded-xl"
            onLoadedMetadata={setPlaybackRate}
          />
          <div className="flex flex-row flex-wrap gap-2">
            {services?.map((service) => (
              <Text
                key={service}
                className="rounded-md border border-jb-primary-400 px-2 py-1 text-jb-neutral-100"
              >
                {service}
              </Text>
            ))}
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col gap-10 py-24 lg:flex-row">
        <div className="flex-1">
          <video
            src={bottomImage}
            autoPlay
            loop
            muted
            playsInline
            className="h-auto w-full rounded-xl"
            onLoadedMetadata={setPlaybackRate}
          />
        </div>
        <div className="flex-1">
          <div className="flex max-w-xl flex-col">
            <Text variant="h3" className="pb-3 text-jb-neutral-100">
              Scope
            </Text>
            <div className="flex flex-col gap-1 border-b border-jb-neutral-800 pb-6">
              {scope}
            </div>
            <div className="flex flex-row pt-6">
              <div className="flex flex-1 flex-col gap-1">
                <Text variant="body-small" className="text-jb-neutral-600">
                  Industry
                </Text>
                <Text className="text-jb-neutral-100">{industry}</Text>
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <Text variant="body-small" className="text-jb-neutral-600">
                  Country
                </Text>
                <Text className="text-jb-neutral-100">{country}</Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Stack>
  );
};

export default ClientPage;
